import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { Badge } from 'reactstrap';
import httpService from "../../services/http.service";

const reasonText = (t, reason) => ({
    never_exhausted: t("Le budget n'a pas été épuisé : pas d'annonce nécessaire."),
    still_exhausted: t("Le budget est encore épuisé : augmentez-le ou attendez la prochaine période."),
    already_announced: t("La réouverture a déjà été annoncée."),
    rate_limited: t("Une annonce a déjà été mise en file depuis moins de 24 h."),
    unavailable: t("Annonce indisponible : mise à jour de la base de données en attente."),
}[reason] || '');

const usd = (value) => `${Number(value || 0).toFixed(2)} $`;

/**
 * Consommation du budget de parrainage + bouton d'annonce de réouverture.
 * Le texte de l'annonce est fixe (FR/EN selon la langue du destinataire) : il n'est pas modifiable ici.
 */
const ReferralBudgetPanel = ({ budget, announcement, onQueued }) => {
    const { t } = useTranslation();
    const [audience, setAudience] = useState('referrers');
    const [counts, setCounts] = useState(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);

    const available = !!(announcement && announcement.available);

    useEffect(() => {
        if (!available) {
            return;
        }
        httpService.get('/referral/announcements')
            .then(({ data }) => setCounts(data?.data?.audiences || null))
            .catch(() => setCounts(null));
    }, [available]);

    if (!budget) {
        return null;
    }

    const percent = Math.min(100, Math.max(0, Number(budget.percent) || 0));
    const barColor = budget.exhausted ? 'bg-danger' : (percent >= 80 ? 'bg-warning' : 'bg-success');

    const queue = async () => {
        const count = counts ? counts[audience] : null;
        if (audience === 'all_active') {
            const ok = window.confirm(t("Envoyer l'annonce à TOUS les utilisateurs actifs ({{count}} destinataires) ?", { count: count ?? '?' }));
            if (!ok) {
                return;
            }
        }
        setBusy(true);
        setMessage(null);
        try {
            await httpService.post('/referral/announcements', {
                audience,
                confirm: audience === 'all_active',
                expected_count: audience === 'all_active' ? count : undefined,
            });
            setMessage({ type: 'success', text: t("Annonce mise en file : l'envoi démarre dans quelques minutes.") });
            onQueued && onQueued();
        } catch (e) {
            const errors = e?.response?.data?.errors;
            const first = errors ? Object.values(errors)[0]?.[0] : null;
            setMessage({ type: 'danger', text: first || t("Impossible de mettre l'annonce en file.") });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-2">
            <div className="d-flex justify-content-between">
                <span>
                    {t('Consommé')} {usd(budget.consumed)} · {t('réservé')} {usd(budget.reserved)} · {t('restant')} <strong>{usd(budget.remaining)}</strong> {t('sur')} {usd(budget.budget)}
                </span>
                {budget.exhausted
                    ? <Badge color="danger">{t("Épuisé")}</Badge>
                    : <Badge color="success">{t("Ouvert")}</Badge>}
            </div>
            <div className="progress mt-1 mb-1" style={{ height: 10 }}>
                <div
                    className={`progress-bar ${barColor}`}
                    role="progressbar"
                    style={{ width: `${percent}%` }}
                    aria-valuenow={percent}
                    aria-valuemin="0"
                    aria-valuemax="100"
                />
            </div>
            <small className="text-muted d-block">
                {percent} % · {t("coût d'un parrainage")} {usd(budget.cost)} · {budget.pending} {t('en attente')} · {budget.lost} {t('perdus (budget / expiration)')}
            </small>

            {announcement && (
                <div className="mt-3">
                    {announcement.available ? (
                        <>
                            <div className="mb-1"><strong>{t("Le budget est de nouveau suffisant.")}</strong> {t('Annoncer la réouverture du parrainage :')}</div>
                            <select className="form-control form-control-sm d-inline-block w-auto mr-2" value={audience} onChange={(e) => setAudience(e.target.value)}>
                                <option value="referrers">{t('Parrains et parrainages en attente')}{counts ? ` (${counts.referrers})` : ''}</option>
                                <option value="all_active">{t('Tous les utilisateurs actifs')}{counts ? ` (${counts.all_active})` : ''}</option>
                            </select>
                            <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={queue}>
                                <i className="fa fa-bullhorn"></i>{' '}
                                <span>{t("Annoncer la réouverture")}</span>
                            </button>
                            <small className="text-muted d-block mt-1">{t("Texte fixe, envoyé dans la langue de chaque utilisateur (anglais par défaut).")}</small>
                        </>
                    ) : (
                        announcement.reason && announcement.reason !== 'never_exhausted' && (
                            <small className="text-muted">{reasonText(t, announcement.reason)}</small>
                        )
                    )}
                    {message && <div className={`mt-2 text-${message.type}`}>{message.text}</div>}
                </div>
            )}
        </div>
    );
};

export default ReferralBudgetPanel;
